"use client"

import React, { useEffect, useMemo, useRef, useState } from 'react'
import BlogSlider from './slider';
import BlogTabs from './blog-tabs';
import { useLocalization } from '@akinon/next/hooks';
import { Button, Icon, Link } from '@theme/components';
import clsx from 'clsx';
import BlogItem from './blog-item';
import Breadcrumb from '@theme/views/breadcrumb';
import { generateBreadcrumbFromUrl } from '@theme/utils/generate-breadcrumb';

function BlogList({data, footerData}: {data: any, footerData: any}) {
  const { t } = useLocalization();
  const [activeTab, setActiveTab] = useState('');
  const [searchText, setSearchText] = useState('');
  const [visibleCount, setVisibleCount] = useState(6);

  const breadcrumbData = generateBreadcrumbFromUrl({ url: '/blog', pk: '', label: 'Yatsan Blog' });
  const blogListRef = useRef<HTMLDivElement>(null);

  const sliders = useMemo(() => {
    return data?.attributes?.blog_banners || [];
  }, [data]);

  const tabs = useMemo(() => {
    return data?.attributes?.categories?.map((cat) => ({
      slug: cat?.value?.slug,
      title: cat?.value?.title
    }));
  }, [data]);

  const socialLinks = useMemo(() => {
    return footerData?.attributes?.social_media || [];
  }, [footerData]);

  const scrollToBlogList = () => {
    blogListRef?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const handleTabChange = (tabSlug: string) => {
    setActiveTab(tabSlug);
    setVisibleCount(6);
    setTimeout(scrollToBlogList, 100);
  };

  useEffect(() => {
    if (searchText?.trim()) {
      scrollToBlogList();
    }
  }, [searchText]);

  const filteredBlogs = useMemo(() => {
    const allBlogs = data?.attributes?.blogs || [];

    return allBlogs?.filter((blog) => {
      const matchesCategory = activeTab
        ? blog?.value?.category?.includes(activeTab)
        : true;

      const matchesSearch = searchText?.trim()
        ? blog?.value?.title?.toLowerCase()?.includes(searchText.toLowerCase()) ||
          blog?.value?.summary?.toLowerCase()?.includes(searchText.toLowerCase())
        : true;

      return matchesCategory && matchesSearch;
    });
  }, [data, activeTab, searchText]);

  const visibleBlogs = filteredBlogs?.slice(0, visibleCount);

  return (
    <div>
      <div className='px-5 lg:px-15'>
        <Breadcrumb breadcrumbList={breadcrumbData} />
      </div>
      {sliders.length > 0 && (
        <div>
          <BlogSlider slides={sliders} />
        </div>
      )}
      <div>
        <BlogTabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={handleTabChange}
          className='flex items-center mx-2.5 border-b border-[#CDCFCE] overflow-x-auto'
        />
      </div>
      <div className='max-w-[1280px] mx-auto'>
        <div className='px-5 pt-12 pb-16 text-black-100'>
          {data?.attributes?.page_title?.value && (
            <h1 className='mb-6 text-[32px] leading-[110%] font-medium'>
              {data?.attributes?.page_title.value}
            </h1>
          )}
          {data?.attributes?.page_description?.value && (
            <p className='text-xl leading-[120%]'>
              {data?.attributes?.page_description?.value}
            </p>
          )}
        </div>

        <div className='flex flex-col lg:flex-row lg:justify-between lg:items-start px-5 gap-10'>
          <div className='border border-[#A5A5A5]/30 h-8 rounded-[5rem] flex items-center justify-between w-full lg:max-w-[250px]'>
            <div className={clsx("flex items-center bg-transparent rounded-[5rem] px-2")}>
              <Icon name="search" size={32} className="mr-2" />
              <input
                className="text-base font-normal placeholder:text-[#A5A5A5] text-primary-black grow outline-0 ring-0 h-6 focus-visible:outline-none bg-transparent"
                placeholder={'Arama'}
                value={searchText}
                onChange={(e) => setSearchText(e?.target?.value)}
              />
            </div>
          </div>

          <div className='flex flex-col lg:flex-row lg:items-center gap-2'>
            <div className='text-sm text-black-100'>
              {t('common.blog.follow_us')}
            </div>
            <ul className="flex items-center gap-2">
              {socialLinks?.map((social, index) => (
                <li key={index}>
                  <Link
                    href={social?.value?.link}
                    rel="nofollow"
                    target={
                      social?.value?.is_target_blank === 'yes'
                        ? '_blank'
                        : '_self'
                    }
                  >
                    <Icon name={social?.value?.icon} size={32} />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className='mt-10 lg:mt-[50px] px-5 scroll-m-64 lg:scroll-m-24' ref={blogListRef}>
          {visibleBlogs?.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-2.5 gap-y-6 lg:gap-x-10 lg:gap-y-20">
              {visibleBlogs?.map((blog, index) => (
                <BlogItem key={index} blog={blog} />
              ))}
            </div>
          ) : (
            <div className="text-center flex items-center justify-center flex-col w-full py-20">
              <Icon name="close" size={48} className="mx-auto text-black-100" />
              <p className="text-sm text-black-100">
                {searchText
                  ? t('common.blog.no_results')
                  : t('common.blog.no_category_results')}
              </p>
            </div>
          )}

          {visibleCount < filteredBlogs?.length && (
            <div className="text-center mt-10">
              <Button
                onClick={() => setVisibleCount((prev) => prev + 6)}
                className="border border-primary-black text-primary-black hover:bg-primary-black hover:text-white"
              >
                {t('common.blog.show_more')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BlogList
